
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle } from "lucide-react";

interface DeletePatientFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: any;
}

export const DeletePatientFileDialog = ({ open, onOpenChange, file }: DeletePatientFileDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!file) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("patient_files")
        .delete()
        .eq("id", file.id);

      if (error) throw error;

      toast({
        title: "Dossier supprimé",
        description: "Le dossier patient a été supprimé avec succès",
      });

      queryClient.invalidateQueries({ queryKey: ["patient-files"] });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error deleting patient file:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le dossier patient: " + error.message,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Supprimer le dossier patient</DialogTitle>
          <DialogDescription>
            Voulez-vous vraiment supprimer le dossier de {file?.first_name} {file?.last_name} ({file?.file_number}) ?
            Cette action est irréversible.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-md text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">
            Toutes les informations associées à ce dossier seront également supprimées.
          </span>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
